import React from 'react';
import { debounce } from 'lodash';

/**
 * ResizeObserverManager
 *
 * A singleton class for managing all ResizeObserver instances in the application.
 * This implementation follows Module M15 (Error Handling) principles by providing
 * comprehensive error handling to prevent uncaught exceptions.
 *
 * It also follows Module M5 (Task Definition) by having clear boundaries and
 * Module M7 (Integration Points) by defining explicit interfaces for integration.
 *
 * The implementation uses RequestAnimationFrame to synchronize with the browser's
 * render cycle and prevent ResizeObserver loop errors.
 */

// Patch the global ResizeObserver to prevent loop errors
const patchGlobalResizeObserver = () => {
  if (typeof window === 'undefined') return;

  // Completely suppress the ResizeObserver error
  const originalConsoleError = console.error;
  console.error = function(...args: any[]) {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
      // Suppress the specific ResizeObserver error
      return;
    }
    // Call the original console.error for all other errors
    return originalConsoleError.apply(console, args);
  };

  // Patch the error event handler to prevent uncaught exceptions from ResizeObserver
  window.addEventListener('error', function(e) {
    if (e && e.message && 
        (e.message.includes('ResizeObserver loop') ||
         e.message.includes('ResizeObserver loop completed with undelivered notifications'))) {
      // Prevent the error from propagating
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);
  
  // Also patch window.onerror as a fallback
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && typeof message === 'string' && 
        (message.includes('ResizeObserver loop') ||
         message.includes('ResizeObserver loop completed with undelivered notifications'))) {
      return true; // Prevent default error handling
    }
    // Call original handler if it exists
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Only patch if ResizeObserver exists
  if (window.ResizeObserver) {
    // Store the original ResizeObserver
    const OriginalResizeObserver = window.ResizeObserver;

    // Override the global ResizeObserver with our patched version
    window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        // Create a wrapped callback that catches errors
        const wrappedCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
          // Wrap the callback execution in a try-catch block
          try {
            // Use requestAnimationFrame to throttle callbacks and prevent loop
            window.requestAnimationFrame(() => {
              try {
                callback(entries, observer);
              } catch (error) {
                console.warn('ResizeObserver callback error:', error);
              }
            });
          } catch (error) {
            // Log the error but don't throw it
            console.warn('ResizeObserver callback error:', error);
          }
        };
        
        // Call the original constructor with the wrapped callback
        super(wrappedCallback);
      }
    };
  }
};

// Apply the global patch immediately
patchGlobalResizeObserver();

// Singleton class for managing all ResizeObserver instances
class ResizeObserverManager {
  private static instance: ResizeObserverManager;
  private observer: ResizeObserver | null = null;
  private elementMap = new Map<Element, Set<(dimensions: DOMRectReadOnly) => void>>();
  private frameId: number | null = null;

  private constructor() {
    this.observer = new ResizeObserver((entries) => {
      if (this.frameId) {
        cancelAnimationFrame(this.frameId);
      }

      this.frameId = requestAnimationFrame(() => {
        try {
          entries.forEach(entry => {
            const callbacks = this.elementMap.get(entry.target);
            if (callbacks) {
              callbacks.forEach(callback => {
                callback(entry.contentRect);
              });
            }
          });
        } catch (error) {
          console.warn('ResizeObserver processing error:', error);
        } finally {
          this.frameId = null;
        }
      });
    });
  }

  public static getInstance(): ResizeObserverManager {
    if (!ResizeObserverManager.instance) {
      ResizeObserverManager.instance = new ResizeObserverManager();
    }
    return ResizeObserverManager.instance;
  }

  public observe(element: Element, callback: (dimensions: DOMRectReadOnly) => void): () => void {
    if (!this.observer) return () => {};

    if (!this.elementMap.has(element)) {
      this.elementMap.set(element, new Set());
      this.observer.observe(element);
    }
    
    this.elementMap.get(element)!.add(callback);

    return () => {
      const callbacks = this.elementMap.get(element);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.elementMap.delete(element);
          this.observer?.unobserve(element);
        }
      }
    };
  }

  public disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.elementMap.clear();
    }
    
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }
}

/**
 * React hook for using the ResizeObserverManager
 * 
 * This hook provides a convenient way to use the ResizeObserverManager in React components.
 * It returns a ref to attach to the element, the current dimensions, and a boolean indicating
 * if the initial measurement has been completed.
 * 
 * @param options Configuration options for the hook
 * @returns [ref, dimensions, isReady]
 */
export function useResizeObserver<T extends HTMLElement = HTMLDivElement>(
  options: {
    debounceTime?: number;
    skipInitial?: boolean;
    onlyWhenVisible?: boolean;
  } = {}
): [React.RefObject<T>, { width: number; height: number }, boolean] {
  const {
    debounceTime = 100,
    skipInitial = false,
    onlyWhenVisible = true
  } = options;
  
  const ref = React.useRef<T>(null);
  const [dimensions, setDimensions] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [isReady, setIsReady] = React.useState(!skipInitial);
  const initialMeasurementDone = React.useRef(false);
  
  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const manager = ResizeObserverManager.getInstance();
    
    const debouncedSetDimensions = debounce((rect: DOMRectReadOnly) => {
      const { width, height } = rect;
      
      if (onlyWhenVisible && (width === 0 || height === 0)) {
        return;
      }
      
      setDimensions({ width, height });
      
      if (!initialMeasurementDone.current) {
        initialMeasurementDone.current = true;
        setIsReady(true);
      }
    }, debounceTime);
    
    const unobserve = manager.observe(element, debouncedSetDimensions);
    
    return () => {
      debouncedSetDimensions.cancel();
      unobserve();
    };
  }, [debounceTime, onlyWhenVisible, skipInitial]);
  
  return [ref, dimensions, isReady];
}

export default ResizeObserverManager;